import { useState, useEffect } from 'react';
import { useAuth, useBusinessUnit } from '@/App';
import { auth, settings } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Lock, Eye, EyeOff, User, Mail, Save, Edit2, Building2, Phone, Globe, MapPin, UserCircle, Home, Briefcase, Camera, Upload, Shield, ShieldCheck, ShieldOff, DollarSign, Send, Server } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('token');

function SmtpSettingsCard() {
  const [smtpData, setSmtpData] = useState({ smtp_host: '', smtp_port: 465, smtp_user: '', smtp_password: '', sender_email: '' });
  const [loading, setLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [passwordSet, setPasswordSet] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    loadSmtpSettings();
  }, []);

  const loadSmtpSettings = async () => {
    try {
      const res = await axios.get(`${API}/api/settings/smtp`, { headers: { Authorization: `Bearer ${getToken()}` } });
      setSmtpData({ smtp_host: res.data.smtp_host, smtp_port: res.data.smtp_port, smtp_user: res.data.smtp_user, smtp_password: '', sender_email: res.data.sender_email });
      setPasswordSet(res.data.smtp_password_set);
    } catch (err) { /* SMTP settings load failed silently */ }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.put(`${API}/api/settings/smtp`, smtpData, { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success('SMTP settings saved!');
      setPasswordSet(true);
      setSmtpData(prev => ({ ...prev, smtp_password: '' }));
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    finally { setLoading(false); }
  };

  const handleTest = async () => {
    if (!testEmail) { toast.error('Enter a test email address'); return; }
    setTestLoading(true);
    try {
      const res = await axios.post(`${API}/api/settings/smtp/test`, { to_email: testEmail }, { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success(res.data.message);
    } catch (err) { toast.error(err.response?.data?.detail || 'Test failed'); }
    finally { setTestLoading(false); }
  };

  return (
    <Card className="border-slate-100 shadow-sm" data-testid="smtp-settings-card">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Mail className="w-5 h-5 text-blue-600" />
          Email / SMTP Settings
        </CardTitle>
        <CardDescription>Configure the email server used for sending statements, notifications, and password resets</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSave} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>SMTP Host</Label>
              <div className="relative">
                <Server className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input value={smtpData.smtp_host} onChange={e => setSmtpData({ ...smtpData, smtp_host: e.target.value })} placeholder="smtp.hostinger.com" className="pl-10" data-testid="smtp-host-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>SMTP Port</Label>
              <Input type="number" value={smtpData.smtp_port} onChange={e => setSmtpData({ ...smtpData, smtp_port: parseInt(e.target.value) || 465 })} placeholder="465" data-testid="smtp-port-input" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>SMTP Username (Email)</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input value={smtpData.smtp_user} onChange={e => setSmtpData({ ...smtpData, smtp_user: e.target.value })} placeholder="hello@utmostfinancial.ca" className="pl-10" data-testid="smtp-user-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>SMTP Password {passwordSet && !smtpData.smtp_password && <span className="text-xs text-emerald-600 ml-1">(saved)</span>}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={smtpData.smtp_password}
                  onChange={e => setSmtpData({ ...smtpData, smtp_password: e.target.value })}
                  placeholder={passwordSet ? '••••••••  (leave blank to keep current)' : 'Enter SMTP password'}
                  className="pl-10 pr-10"
                  data-testid="smtp-password-input"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Sender Email (From address)</Label>
            <div className="relative">
              <Send className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input value={smtpData.sender_email} onChange={e => setSmtpData({ ...smtpData, sender_email: e.target.value })} placeholder="hello@utmostfinancial.ca" className="pl-10" data-testid="smtp-sender-input" />
            </div>
          </div>

          <Button type="submit" disabled={loading} className="bg-slate-900 hover:bg-slate-800" data-testid="smtp-save-btn">
            <Save className="w-4 h-4 mr-2" />
            {loading ? 'Saving...' : 'Save SMTP Settings'}
          </Button>
        </form>

        {/* Test Email */}
        <div className="mt-6 pt-4 border-t border-slate-200">
          <Label className="text-sm font-medium text-slate-700 mb-2 block">Test Connection</Label>
          <div className="flex gap-2">
            <Input value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="Enter email to receive test" className="flex-1" data-testid="smtp-test-email-input" />
            <Button type="button" onClick={handleTest} disabled={testLoading} variant="outline" data-testid="smtp-test-btn">
              <Send className="w-4 h-4 mr-2" />
              {testLoading ? 'Sending...' : 'Send Test'}
            </Button>
          </div>
          <p className="text-xs text-slate-500 mt-2">Sends a test email to verify your SMTP settings are working correctly.</p>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Settings() {
  const { user, login } = useAuth();
  const { businessUnit } = useBusinessUnit();
  const isAgent = user?.role === 'agent';
  const isAdmin = user?.role === 'admin';
  
  // Business unit labels
  const buLabel = businessUnit === 'insurance' ? 'Insurance & Investment' : 'Mortgage';
  const BuIcon = businessUnit === 'insurance' ? Briefcase : Home;
  
  const [loading, setLoading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [agentProfileLoading, setAgentProfileLoading] = useState(false);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [isEditingCompany, setIsEditingCompany] = useState(false);
  const [isEditingAgentProfile, setIsEditingAgentProfile] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Currency state
  const [currencies, setCurrencies] = useState([]);
  const [defaultCurrency, setDefaultCurrency] = useState({ code: 'CAD', symbol: '$', name: 'Canadian Dollar' });
  const [currencyLoading, setCurrencyLoading] = useState(false);
  
  const [profileData, setProfileData] = useState({
    name: '',
    email: ''
  });

  const [agentProfileData, setAgentProfileData] = useState({
    name: '',
    email: '',
    address: '',
    phone: '',
    photo: null
  });

  const [originalAgentProfile, setOriginalAgentProfile] = useState({});
  const [photoUploading, setPhotoUploading] = useState(false);

  const [companyData, setCompanyData] = useState({
    company_name: '',
    address: '',
    phone: '',
    email: '',
    website: ''
  });

  const [originalCompanyData, setOriginalCompanyData] = useState({});
  
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // 2FA State
  const [twoFAStatus, setTwoFAStatus] = useState({ enabled: false, method: 'email' });
  const [twoFALoading, setTwoFALoading] = useState(false);
  const [showTwoFAModal, setShowTwoFAModal] = useState(false);
  const [twoFAAction, setTwoFAAction] = useState('enable'); // 'enable' or 'disable'
  const [verificationCode, setVerificationCode] = useState('');

  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || '',
        email: user.email || ''
      });
      loadTwoFAStatus();
    }
    if (isAgent) {
      loadAgentProfile();
    } else {
      loadCompanySettings();
      loadCurrencies();
    }
  }, [user, isAgent, businessUnit]); // Reload when business unit changes

  const loadCurrencies = async () => {
    const token = getToken();
    try {
      const [currenciesRes, defaultRes] = await Promise.all([
        axios.get(`${API}/api/currencies`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/currencies/default`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setCurrencies(currenciesRes.data);
      setDefaultCurrency(defaultRes.data);
    } catch (err) {
      toast.error('Failed to load currency settings');
    }
  };

  const handleCurrencyChange = async (newCurrency) => {
    setCurrencyLoading(true);
    const token = getToken();
    
    try {
      const response = await axios.put(
        `${API}/api/currencies/default`,
        { default_currency: newCurrency },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDefaultCurrency({
        code: response.data.code,
        symbol: response.data.symbol,
        name: response.data.name
      });
      toast.success('Default currency updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update currency');
    } finally {
      setCurrencyLoading(false);
    }
  };

  const loadTwoFAStatus = async () => {
    const token = getToken();
    try {
      const response = await axios.get(`${API}/api/auth/2fa/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTwoFAStatus({
        enabled: response.data.two_factor_enabled,
        method: response.data.two_factor_method || 'email'
      });
    } catch (err) {
      toast.error('Failed to load 2FA status');
    }
  };

  const handleTwoFAToggle = async () => {
    const action = twoFAStatus.enabled ? 'disable' : 'enable';
    setTwoFAAction(action);
    setTwoFALoading(true);
    const token = getToken();
    
    try {
      const response = await axios.post(`${API}/api/auth/2fa/${action}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.requires_verification) {
        setShowTwoFAModal(true);
        toast.info('Verification code sent to your email');
      } else {
        toast.error(response.data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to process request');
    } finally {
      setTwoFALoading(false);
    }
  };

  const handleVerifyTwoFA = async () => {
    if (!verificationCode.trim() || verificationCode.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }

    setTwoFALoading(true);
    const token = getToken();
    
    try {
      const response = await axios.post(`${API}/api/auth/2fa/verify?code=${verificationCode}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message);
      setShowTwoFAModal(false);
      setVerificationCode('');
      loadTwoFAStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid verification code');
    } finally {
      setTwoFALoading(false);
    }
  };

  const loadAgentProfile = async () => {
    try {
      const response = await auth.getAgentProfile();
      setAgentProfileData(response.data);
      setOriginalAgentProfile(response.data);
    } catch (err) {
      toast.error('Failed to load agent profile');
    }
  };

  const loadCompanySettings = async () => {
    try {
      const response = await settings.getCompany();
      setCompanyData(response.data);
      setOriginalCompanyData(response.data);
    } catch (err) {
      toast.error('Failed to load company settings');
    }
  };

  const handleProfileChange = (e) => {
    setProfileData({ ...profileData, [e.target.name]: e.target.value });
  };

  const handleAgentProfileChange = (e) => {
    setAgentProfileData({ ...agentProfileData, [e.target.name]: e.target.value });
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image size must be less than 2MB');
      return;
    }

    setPhotoUploading(true);
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64 = event.target?.result;
        setAgentProfileData({ ...agentProfileData, photo: base64 });
        setPhotoUploading(false);
        toast.success('Photo uploaded! Click Save to update your profile.');
      };
      reader.onerror = () => {
        toast.error('Failed to read image file');
        setPhotoUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      toast.error('Failed to upload photo');
      setPhotoUploading(false);
    }
  };

  const removePhoto = () => {
    setAgentProfileData({ ...agentProfileData, photo: null });
  };

  const handleCompanyChange = (e) => {
    setCompanyData({ ...companyData, [e.target.name]: e.target.value });
  };

  const handlePasswordChange = (e) => {
    setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
  };

  const handleAgentProfileSubmit = async (e) => {
    e.preventDefault();
    
    if (!agentProfileData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    if (!agentProfileData.email.trim()) {
      toast.error('Email is required');
      return;
    }

    setAgentProfileLoading(true);
    try {
      const response = await auth.updateAgentProfile(agentProfileData);
      setOriginalAgentProfile(response.data);
      
      // Update auth context with new user data
      const token = localStorage.getItem('token');
      login({ ...user, name: response.data.name, email: response.data.email }, token);
      
      toast.success('Profile updated successfully');
      setIsEditingAgentProfile(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setAgentProfileLoading(false);
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    
    if (!profileData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    if (!profileData.email.trim()) {
      toast.error('Email is required');
      return;
    }

    setProfileLoading(true);
    try {
      const response = await auth.updateProfile({
        name: profileData.name,
        email: profileData.email
      });
      
      const token = localStorage.getItem('token');
      login(response.data, token);
      
      toast.success('Profile updated successfully');
      setIsEditingProfile(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setProfileLoading(false);
    }
  };

  const handleCompanySubmit = async (e) => {
    e.preventDefault();
    
    if (!companyData.company_name.trim()) {
      toast.error('Company name is required');
      return;
    }

    setCompanyLoading(true);
    try {
      await settings.updateCompany(companyData);
      setOriginalCompanyData(companyData);
      toast.success('Company information updated successfully');
      setIsEditingCompany(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update company information');
    } finally {
      setCompanyLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      await auth.changePassword({
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      });
      toast.success('Password changed successfully');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const cancelProfileEdit = () => {
    setProfileData({
      name: user?.name || '',
      email: user?.email || ''
    });
    setIsEditingProfile(false);
  };

  const cancelAgentProfileEdit = () => {
    setAgentProfileData(originalAgentProfile);
    setIsEditingAgentProfile(false);
  };

  const cancelCompanyEdit = () => {
    setCompanyData(originalCompanyData);
    setIsEditingCompany(false);
  };

  return (
    <div className="space-y-8 animate-fadeIn max-w-2xl" data-testid="settings-page">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-1">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${businessUnit === 'insurance' ? 'bg-blue-100' : 'bg-emerald-100'}`}>
            <BuIcon className={`w-5 h-5 ${businessUnit === 'insurance' ? 'text-blue-600' : 'text-emerald-600'}`} />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>
              Settings
            </h1>
            <p className="text-slate-500">
              {isAgent ? 'Manage your personal details and login information' : `${buLabel} Division - Manage company and account settings`}
            </p>
          </div>
        </div>
      </div>

      {/* Agent Profile (for agents only) */}
      {isAgent && (
        <Card className="border-slate-100 shadow-sm">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <UserCircle className="w-5 h-5 text-emerald-600" />
                  Personal Details
                </CardTitle>
                <CardDescription>Your agent profile information</CardDescription>
              </div>
              {!isEditingAgentProfile && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsEditingAgentProfile(true)}
                  data-testid="edit-agent-profile-btn"
                >
                  <Edit2 className="w-4 h-4 mr-2" /> Edit
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {isEditingAgentProfile ? (
              <form onSubmit={handleAgentProfileSubmit} className="space-y-4">
                {/* Photo Upload Section */}
                <div className="space-y-2">
                  <Label>Profile Photo</Label>
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      {agentProfileData.photo ? (
                        <img 
                          src={agentProfileData.photo} 
                          alt="Profile" 
                          className="w-24 h-24 rounded-full object-cover border-2 border-slate-200"
                        />
                      ) : (
                        <div className="w-24 h-24 rounded-full bg-slate-200 flex items-center justify-center">
                          <User className="w-10 h-10 text-slate-400" />
                        </div>
                      )}
                      {photoUploading && (
                        <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center">
                          <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent"></div>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handlePhotoUpload}
                          className="hidden"
                          data-testid="photo-upload-input"
                        />
                        <span className="inline-flex items-center px-3 py-2 text-sm font-medium text-emerald-700 bg-emerald-100 rounded-lg hover:bg-emerald-200 transition-colors">
                          <Upload className="w-4 h-4 mr-2" />
                          Upload Photo
                        </span>
                      </label>
                      {agentProfileData.photo && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={removePhoto}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          Remove Photo
                        </Button>
                      )}
                      <p className="text-xs text-slate-500">Max 2MB, JPG/PNG</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="agent_name">Full Name *</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="agent_name"
                      name="name"
                      value={agentProfileData.name}
                      onChange={handleAgentProfileChange}
                      placeholder="Enter your full name"
                      required
                      data-testid="agent-name-input"
                      className="pl-10"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="agent_email">Email *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="agent_email"
                      name="email"
                      type="email"
                      value={agentProfileData.email}
                      onChange={handleAgentProfileChange}
                      placeholder="Enter your email"
                      required
                      data-testid="agent-email-input"
                      className="pl-10"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="agent_address">Address</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                    <Textarea
                      id="agent_address"
                      name="address"
                      value={agentProfileData.address}
                      onChange={handleAgentProfileChange}
                      placeholder="Enter your address"
                      data-testid="agent-address-input"
                      className="pl-10 min-h-[80px]"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="agent_phone">Phone</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="agent_phone"
                      name="phone"
                      value={agentProfileData.phone}
                      onChange={handleAgentProfileChange}
                      placeholder="Enter your phone number"
                      data-testid="agent-phone-input"
                      className="pl-10"
                    />
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button 
                    type="submit" 
                    disabled={agentProfileLoading}
                    data-testid="save-agent-profile-btn"
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  >
                    {agentProfileLoading ? 'Saving...' : <><Save className="w-4 h-4 mr-2" /> Save Changes</>}
                  </Button>
                  <Button 
                    type="button"
                    variant="outline"
                    onClick={cancelAgentProfileEdit}
                    data-testid="cancel-agent-profile-btn"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            ) : (
              <div className="space-y-4">
                {/* Display Photo */}
                <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                  {agentProfileData.photo ? (
                    <img 
                      src={agentProfileData.photo} 
                      alt="Profile" 
                      className="w-16 h-16 rounded-full object-cover border-2 border-slate-200"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-slate-200 flex items-center justify-center">
                      <User className="w-8 h-8 text-slate-400" />
                    </div>
                  )}
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Profile Photo</p>
                    <p className="text-slate-900 font-medium">{agentProfileData.photo ? 'Photo uploaded' : 'No photo'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <User className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Full Name</p>
                    <p className="text-slate-900 font-medium">{agentProfileData.name || 'Not set'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <Mail className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Email</p>
                    <p className="text-slate-900 font-medium">{agentProfileData.email || 'Not set'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                  <MapPin className="w-5 h-5 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Address</p>
                    <p className="text-slate-900 font-medium whitespace-pre-wrap">{agentProfileData.address || 'Not set'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <Phone className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Phone</p>
                    <p className="text-slate-900 font-medium">{agentProfileData.phone || 'Not set'}</p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Company Information (for admins only) */}
      {!isAgent && (
        <Card className="border-slate-100 shadow-sm">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-slate-600" />
                  {buLabel} Company Information
                </CardTitle>
                <CardDescription>Company details for {buLabel.toLowerCase()} statements and documents</CardDescription>
              </div>
              {!isEditingCompany && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsEditingCompany(true)}
                  data-testid="edit-company-btn"
                >
                  <Edit2 className="w-4 h-4 mr-2" /> Edit
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {isEditingCompany ? (
              <form onSubmit={handleCompanySubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="company_name">Company Name</Label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="company_name"
                      name="company_name"
                      value={companyData.company_name}
                      onChange={handleCompanyChange}
                      placeholder="Enter company name"
                      required
                      data-testid="company-name-input"
                      className="pl-10"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="address">Address</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                    <Textarea
                      id="address"
                      name="address"
                      value={companyData.address}
                      onChange={handleCompanyChange}
                      placeholder="Enter company address"
                      data-testid="company-address-input"
                      className="pl-10 min-h-[80px]"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="phone"
                        name="phone"
                        value={companyData.phone}
                        onChange={handleCompanyChange}
                        placeholder="Enter phone number"
                        data-testid="company-phone-input"
                        className="pl-10"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="company_email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="company_email"
                        name="email"
                        type="email"
                        value={companyData.email}
                        onChange={handleCompanyChange}
                        placeholder="Enter company email"
                        data-testid="company-email-input"
                        className="pl-10"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="website">Website (Optional)</Label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="website"
                      name="website"
                      value={companyData.website}
                      onChange={handleCompanyChange}
                      placeholder="https://www.example.com"
                      data-testid="company-website-input"
                      className="pl-10"
                    />
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button 
                    type="submit" 
                    disabled={companyLoading}
                    data-testid="save-company-btn"
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  >
                    {companyLoading ? 'Saving...' : <><Save className="w-4 h-4 mr-2" /> Save Changes</>}
                  </Button>
                  <Button 
                    type="button"
                    variant="outline"
                    onClick={cancelCompanyEdit}
                    data-testid="cancel-company-btn"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <Building2 className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Company Name</p>
                    <p className="text-slate-900 font-medium">{companyData.company_name || 'Not set'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                  <MapPin className="w-5 h-5 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Address</p>
                    <p className="text-slate-900 font-medium whitespace-pre-wrap">{companyData.address || 'Not set'}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <Phone className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider">Phone</p>
                      <p className="text-slate-900 font-medium">{companyData.phone || 'Not set'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <Mail className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider">Email</p>
                      <p className="text-slate-900 font-medium">{companyData.email || 'Not set'}</p>
                    </div>
                  </div>
                </div>
                {companyData.website && (
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <Globe className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider">Website</p>
                      <p className="text-slate-900 font-medium">{companyData.website}</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Currency Settings (for admins only) */}
      {isAdmin && (
        <Card className="border-slate-100 shadow-sm">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-emerald-600" />
                  Currency Settings
                </CardTitle>
                <CardDescription>Set the default currency for displaying monetary values</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <Label>Default Currency</Label>
                  <p className="text-xs text-slate-500 mt-1">
                    All amounts in the application will be displayed in this currency
                  </p>
                </div>
                <div className="w-48">
                  <Select
                    value={defaultCurrency.code}
                    onValueChange={handleCurrencyChange}
                    disabled={currencyLoading}
                  >
                    <SelectTrigger data-testid="currency-select">
                      <SelectValue>
                        <span className="flex items-center gap-2">
                          <span className="font-mono">{defaultCurrency.symbol}</span>
                          <span>{defaultCurrency.code}</span>
                        </span>
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {currencies.map((currency) => (
                        <SelectItem key={currency.code} value={currency.code}>
                          <span className="flex items-center gap-2">
                            <span className="font-mono w-6">{currency.symbol}</span>
                            <span>{currency.code}</span>
                            <span className="text-slate-400 text-sm">- {currency.name}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-sm text-slate-600">
                  <strong>Current:</strong> {defaultCurrency.name} ({defaultCurrency.symbol})
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Example: {defaultCurrency.symbol}1,234.56
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* SMTP / Email Settings (admin only) */}
      {isAdmin && <SmtpSettingsCard />}

      {/* Account Info (for admins only - agents use Personal Details above) */}
      {!isAgent && (
        <Card className="border-slate-100 shadow-sm">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <User className="w-5 h-5 text-slate-600" />
                  Account Information
                </CardTitle>
                <CardDescription>Your personal account details</CardDescription>
              </div>
              {!isEditingProfile && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsEditingProfile(true)}
                  data-testid="edit-profile-btn"
                >
                  <Edit2 className="w-4 h-4 mr-2" /> Edit
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {isEditingProfile ? (
              <form onSubmit={handleProfileSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="name"
                      name="name"
                      value={profileData.name}
                      onChange={handleProfileChange}
                      placeholder="Enter your name"
                      required
                      data-testid="profile-name-input"
                      className="pl-10"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={profileData.email}
                      onChange={handleProfileChange}
                      placeholder="Enter your email"
                      required
                      data-testid="profile-email-input"
                      className="pl-10"
                    />
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button 
                    type="submit" 
                    disabled={profileLoading}
                    data-testid="save-profile-btn"
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  >
                    {profileLoading ? 'Saving...' : <><Save className="w-4 h-4 mr-2" /> Save Changes</>}
                  </Button>
                  <Button 
                    type="button"
                    variant="outline"
                    onClick={cancelProfileEdit}
                    data-testid="cancel-profile-btn"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <User className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Name</p>
                    <p className="text-slate-900 font-medium">{user?.name || 'Admin User'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <Mail className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Email</p>
                    <p className="text-slate-900 font-medium">{user?.email}</p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Two-Factor Authentication */}
      <Card className="border-slate-100 shadow-sm">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Shield className="w-5 h-5 text-slate-600" />
                Two-Factor Authentication
              </CardTitle>
              <CardDescription>Add an extra layer of security to your account</CardDescription>
            </div>
            <Badge variant={twoFAStatus.enabled ? "default" : "secondary"} className={twoFAStatus.enabled ? "bg-emerald-100 text-emerald-800" : ""}>
              {twoFAStatus.enabled ? (
                <><ShieldCheck className="w-3 h-3 mr-1" /> Enabled</>
              ) : (
                <><ShieldOff className="w-3 h-3 mr-1" /> Disabled</>
              )}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-slate-500 mt-0.5" />
                <div>
                  <p className="font-medium text-slate-900">Email Verification</p>
                  <p className="text-sm text-slate-500">
                    When enabled, you&apos;ll receive a 6-digit code via email each time you log in.
                  </p>
                </div>
              </div>
            </div>
            
            <Button 
              onClick={handleTwoFAToggle}
              disabled={twoFALoading}
              variant={twoFAStatus.enabled ? "destructive" : "default"}
              className={!twoFAStatus.enabled ? "bg-emerald-600 hover:bg-emerald-700" : ""}
              data-testid="toggle-2fa-btn"
            >
              {twoFALoading ? 'Processing...' : (
                twoFAStatus.enabled ? (
                  <><ShieldOff className="w-4 h-4 mr-2" /> Disable 2FA</>
                ) : (
                  <><ShieldCheck className="w-4 h-4 mr-2" /> Enable 2FA</>
                )
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* 2FA Verification Modal */}
      <Dialog open={showTwoFAModal} onOpenChange={setShowTwoFAModal}>
        <DialogContent className="max-w-md" data-testid="2fa-verification-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Verify Your Identity
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-600">
              We&apos;ve sent a 6-digit verification code to your email address. 
              Enter it below to {twoFAAction === 'enable' ? 'enable' : 'disable'} two-factor authentication.
            </p>
            <div className="space-y-2">
              <Label htmlFor="verification-code">Verification Code</Label>
              <Input
                id="verification-code"
                type="text"
                maxLength={6}
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                placeholder="Enter 6-digit code"
                className="text-center text-2xl tracking-[0.5em] font-mono"
                data-testid="2fa-code-input"
              />
            </div>
            <p className="text-xs text-slate-500">
              The code will expire in 10 minutes.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowTwoFAModal(false);
              setVerificationCode('');
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleVerifyTwoFA}
              disabled={twoFALoading || verificationCode.length !== 6}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="verify-2fa-btn"
            >
              {twoFALoading ? 'Verifying...' : 'Verify'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Password (for both agents and admins) */}
      <Card className="border-slate-100 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Lock className="w-5 h-5 text-slate-600" />
            Change Password
          </CardTitle>
          <CardDescription>Update your password to keep your account secure</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            {/* Current Password */}
            <div className="space-y-2">
              <Label htmlFor="currentPassword">Current Password</Label>
              <div className="relative">
                <Input
                  id="currentPassword"
                  name="currentPassword"
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange}
                  placeholder="Enter current password"
                  required
                  data-testid="current-password-input"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* New Password */}
            <div className="space-y-2">
              <Label htmlFor="newPassword">New Password</Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  name="newPassword"
                  type={showNewPassword ? 'text' : 'password'}
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  placeholder="Enter new password (min 6 characters)"
                  required
                  data-testid="new-password-input"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  placeholder="Confirm new password"
                  required
                  data-testid="confirm-password-input"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button 
              type="submit" 
              disabled={loading}
              data-testid="change-password-btn"
              className="w-full bg-slate-900 hover:bg-slate-800"
            >
              {loading ? 'Changing Password...' : 'Change Password'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
